/*
 * O perfil do Canela sobre o UserPage do core.
 *
 * O core desenha o perfil como um hero colorido de largura total e uma coluna
 * de navegação à esquerda. Aqui tudo vai para dentro do cartão: capa no
 * topo, avatar grande, nomes, uma linha de estatísticas e as abas embaixo.
 * O agente da casca já move o hero para dentro do cartão (PageStructure);
 * aqui o UserCard ganha as peças que faltam e a navegação vira abas.
 *
 * O cabeçalho completo (capa, bio, estatísticas) só
 * aparece no Resumo; nas outras abas ele encolhe para avatar pequeno e nomes
 * (`.collapsed-info`), e a lista ocupa a tela.
 *
 * Tudo só vale para o cartão do perfil (`.UserHero`): o mesmo UserCard
 * aparece como popover nos posts, e lá fica como está.
 */
import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import UserPage from 'flarum/forum/components/UserPage';
import UserCard from 'flarum/forum/components/UserCard';
import UserControls from 'flarum/forum/utils/UserControls';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import Icon from 'flarum/common/components/Icon';
import LinkButton from 'flarum/common/components/LinkButton';
import listItems from 'flarum/common/helpers/listItems';
import classList from 'flarum/common/utils/classList';
import ItemList from 'flarum/common/utils/ItemList';
import PostListItem from 'flarum/forum/components/PostListItem';
import UserStats from './UserStats';
import streamItem from './streamItem';
import ScrollNav from './activity/ScrollNav';
import { ACTIVITY_ORDER } from './activity/install';
import { forumBool, t } from '../utils/settings';
import { thirdParty } from '../thirdparty/settings';
import { currentTab, tabUrl, TAB_ICONS, visibleTabs } from './preferences/tabs';
import { isTagsEnabled } from '../utils/tags';
import PostListState from 'flarum/forum/states/PostListState';
import { userIcons } from '../utils/userIcons';
import { userCoverUrl } from '../features/profile';

/** Itens do UserCard que a linha de estatísticas passa a mostrar. */
const INFO_MOVED_TO_STATS = ['lastSeen', 'joined'];

/**
 * As abas de primeiro nível (Resumo, Atividade, Notificações,
 * Mensagens, Convites, Emblemas, Preferências). Todo o resto — postagens,
 * tópicos, curtidas, menções e o que as extensões acrescentarem — vai para
 * a segunda linha, dentro de "Atividade".
 */
const PRIMARY_NAV_KEYS = ['summary', 'badges', 'settings', 'security', 'notifications', 'messages', 'invites'];

/** Páginas do perfil que, para o próprio usuário, são abas das preferências. */
const PREFERENCE_PAGES = ['security', 'ignored-users'];

/** As abas de "Atividade" com o nome e o ícone do tema. */
const ACTIVITY_LABELS = {
  posts: {
    icon: 'fas fa-bars',
    label: 'user.nav.posts'
  },
  discussions: {
    icon: 'fas fa-list-ul',
    label: 'user.nav.discussions'
  },
  likes: {
    icon: 'far fa-heart'
  },
  mentions: {
    icon: 'fas fa-at'
  }
};
function isProfileHero(card) {
  return /\bUserHero\b/.test(card.attrs?.className || '');
}
function statsEnabled() {
  return forumBool('canelaUserStats', true);
}
function summaryEnabled() {
  return forumBool('canelaUserSummary', true);
}

/** Fora do Resumo o cabeçalho encolhe (`.collapsed-info`). */
export function profileCollapsed() {
  return summaryEnabled() && app.current.get('routeName') !== 'user.summary';
}

/** O grupo cujo ícone vira o "flair" do avatar: o primeiro com ícone. */
function flairGroup(user) {
  const groups = (user.groups() || []).filter(group => !!group);
  return groups.find(group => !!group.icon()) || null;
}
export default function installProfile() {
  extendUserPage();
  extendProfileHero();
  extendUserStream();
}

/**
 * As abas de postagens do perfil (Postagens, Curtidas, Menções) como itens
 * de atividade: avatar de 48px, o título do tópico como
 * link, categoria e data embaixo e o começo do texto — sem o post inteiro com
 * seus botões, que é o que o PostListItem do core desenha.
 */
function extendUserStream() {
  // A categoria do item ("Bug • 5 dias atrás") vem das etiquetas do tópico,
  // que a lista de postagens do core não inclui.
  extend(PostListState.prototype, 'requestParams', function (params) {
    if (!isTagsEnabled() || !params) return;
    const include = typeof params.include === 'string' ? params.include.split(',') : Array.isArray(params.include) ? params.include : [];
    if (!include.includes('discussion.tags')) params.include = [...include, 'discussion.tags'];
  });
  override(PostListItem.prototype, 'view', function (original) {
    const post = this.attrs.post;
    const discussion = post.discussion();
    if (!/^user(\.|$)/.test(app.current.get('routeName') || '') || !discussion) return original();
    return streamItem(post);
  });
}
function extendUserPage() {
  // A classe própria liga o layout em coluna (cartão, abas, conteúdo) sem
  // depender do `Page--vertical` do core, que foi pensado para o IndexPage.
  extend(UserPage.prototype, 'view', function (vnode) {
    if (!vnode || !vnode.attrs) return;
    vnode.attrs.className = classList(vnode.attrs.className, 'CanelaUserPage', {
      'CanelaUserPage--cover': forumBool('canelaUserCover', true),
      'CanelaUserPage--collapsed': profileCollapsed()
    });
  });

  // Sem o AffixedSidebar nem o SelectDropdown: duas linhas de abas no fluxo,
  // abaixo do cartão.
  override(UserPage.prototype, 'sidebar', function () {
    const nav = this.navItems();
    const primary = new ItemList();
    const activity = new ItemList();
    const self = !!this.user && this.user === app.session.user;
    const prefTab = self ? currentTab() : null;
    Object.entries(nav.toObject()).forEach(([key, item]) => {
      if (/separator/i.test(key)) return;
      // Segurança e Ignorados viram abas das preferências.
      if (self && PREFERENCE_PAGES.includes(key)) return;
      const content = item.content;

      // "Configurações" é a entrada das preferências: abre na aba Conta e
      // fica ativa em todas as abas.
      if (key === 'settings' && self && content?.attrs) {
        content.attrs.href = tabUrl(this.user, 'account');
        content.attrs.active = !!prefTab;
        content.children = [t('user.preferences.nav')];
      }
      const relabel = ACTIVITY_LABELS[key];

      // Os nomes e ícones do tema
      // ("Postagens", "Tópicos", o coração das curtidas) no lugar dos do core.
      if (relabel && content?.attrs) {
        content.attrs.icon = relabel.icon;
        if (relabel.label) content.children = [t(relabel.label)];
      }
      if (PRIMARY_NAV_KEYS.includes(key)) {
        primary.add(key, content, item.priority);
        return;
      }

      // A ordem do tema (Tópicos, Respostas, Lidos, Rascunhos, Curtidas,
      // Favoritos, Reações, Votos, Resolvido…); as abas de outras extensões
      // vêm depois, na ordem delas.
      const order = ACTIVITY_ORDER.indexOf(key);
      activity.add(key, content, order === -1 ? item.priority : 1000 - order);
    });
    const isActive = content => !!content?.attrs?.href && LinkButton.isActive(content.attrs);
    const activityActive = activity.toArray().some(isActive);
    if (!activity.isEmpty() && this.user) {
      primary.add('activity', m(LinkButton, {
        href: app.route('user.activity', {
          username: this.user.slug()
        }),
        icon: "fas fa-bars",
        active: activityActive
      }, t('user.nav.activity')), 105);
    }
    return m("nav", {
      className: "UserPage-nav CanelaUserNav"
    }, m("ul", {
      className: "CanelaUserNav-primary"
    }, listItems(primary.toArray())), activityActive && m(ScrollNav, {
      className: "CanelaUserNav-secondary"
    }, listItems(activity.toArray())), prefTab && m("ul", {
      className: "CanelaUserNav-secondary CanelaUserNav-prefs"
    }, visibleTabs(this.user).map(tab => m("li", {
      className: `item-${tab}`
    }, m(LinkButton, {
      href: tabUrl(this.user, tab),
      icon: TAB_ICONS[tab],
      active: tab === prefTab
    }, t(`user.preferences.tabs.${tab}`))))));
  });
}
function extendProfileHero() {
  extend(UserCard.prototype, 'view', function (vnode) {
    if (!isProfileHero(this) || !vnode?.attrs) return;
    const user = this.attrs.user;
    const collapsed = profileCollapsed();
    // Sem imagem de capa enviada, a faixa não aparece: o perfil fica com o
    // topo limpo, em vez de um degradê genérico (pedido do usuário).
    const cover = forumBool('canelaUserCover', true) && !collapsed && !!userCoverUrl(user);
    vnode.attrs.className = classList(vnode.attrs.className, 'CanelaUserHero', {
      'CanelaUserHero--cover': cover,
      'CanelaUserHero--collapsed': collapsed
    });
    const children = Array.isArray(vnode.children) ? vnode.children : [vnode.children];
    if (cover) children.unshift(m("div", {
      className: "CanelaUserHero-cover",
      "aria-hidden": "true"
    }));
    if (statsEnabled() && !collapsed) children.push(m(UserStats, {
      user: user
    }));
    vnode.children = children;
  });

  // O "flair": o ícone do grupo num círculo na cor dele, no canto
  // do avatar.
  extend(UserCard.prototype, 'profileItems', function (items) {
    if (!isProfileHero(this) || !items.has('avatar')) return;
    const group = flairGroup(this.attrs.user);
    items.setContent('avatar', m("div", {
      className: "CanelaUserHero-avatar"
    }, items.get('avatar'), group && m("span", {
      className: classList('CanelaAvatarFlair', {
        'CanelaAvatarFlair--colored': !!group.color()
      }),
      style: group.color() ? {
        '--group-color': group.color()
      } : undefined,
      title: group.nameSingular()
    }, m(Icon, {
      name: group.icon()
    }))));
  });
  extend(UserCard.prototype, 'infoItems', function (items) {
    if (!isProfileHero(this) || !statsEnabled()) return;
    INFO_MOVED_TO_STATS.forEach(key => items.has(key) && items.remove(key));
  });
  extend(UserCard.prototype, 'contentItems', function (items) {
    if (!isProfileHero(this)) return;
    const user = this.attrs.user;
    const groups = (user.groups() || []).filter(group => !!group);

    // Os grupos vão para a linha de estatísticas ("Grupos Admin, …") e o
    // primeiro com ícone vira o flair do avatar; as insígnias redondas que
    // sobram (suspenso, por exemplo) ficam ao lado do nome, não soltas no
    // meio do perfil.
    const badges = user.badges();
    groups.forEach(group => badges.has(`group${group.id()}`) && badges.remove(`group${group.id()}`));
    const badgeList = badges.isEmpty() ? null : m("ul", {
      className: "UserCard-badges badges CanelaUserHero-badges"
    }, listItems(badges.toArray()));
    if (items.has('identity')) {
      items.setContent('identity', identity(user, badgeList));
    }
    if (items.has('badges')) items.remove('badges');
    if (items.has('info') && this.infoItems().isEmpty()) items.remove('info');
  });
  extend(UserCard.prototype, 'controlsItems', function (items) {
    if (!isProfileHero(this)) return;
    const user = this.attrs.user;
    const controls = UserControls.controls(user, this);

    // "Mensagem" (flarum/messages) sai do menu e vira o botão primário à
    // direita.
    if (controls.has('sendMessage')) {
      const message = controls.get('sendMessage');
      controls.remove('sendMessage');
      items.add('canelaMessage', m(Button, Object.assign({}, message?.attrs || {}, {
        className: "Button Button--primary CanelaUserHero-message"
      }), message?.children), 110);
    }

    // O "Chat" do ramon/chat também sai do menu: botão ao lado da "Mensagem".
    if (thirdParty.ramonChatUserButton() && controls.has('chatDirect')) {
      const chat = controls.get('chatDirect');
      controls.remove('chatDirect');
      items.add('canelaChat', m(Button, Object.assign({}, chat?.attrs || {}, {
        className: "Button CanelaUserHero-chat",
        icon: "fas fa-comment-dots"
      }), t('chat.start_conversation')), 111);
    }
    const remaining = controls.toArray().filter(item => !/Separator$/.test(item.itemName || ''));
    if (!remaining.length) {
      items.has('controls') && items.remove('controls');
      return;
    }

    // O menu de controles vira um botão redondo de ícone (⋮).
    const dropdown = m(Dropdown, {
      className: "App-primaryControl CanelaUserHero-controls",
      menuClassName: "Dropdown-menu--right",
      buttonClassName: "Button Button--icon",
      label: app.translator.trans('core.forum.user_controls.button'),
      accessibleToggleLabel: app.translator.trans('core.forum.user_controls.toggle_dropdown_accessible_label'),
      icon: "fas fa-ellipsis-h"
    }, controls.toArray());
    if (items.has('controls')) items.setContent('controls', dropdown);else items.add('controls', dropdown, 100);
  });
}

/**
 * O nome de exibição (o apelido do flarum/nicknames, como nos posts) enorme em
 * negrito, na mesma linha os ícones (selo de verificado, utils/userIcons) e as
 * insígnias; se for diferente, o username embaixo.
 */
function identity(user, badges = null) {
  const username = user.username();
  const displayName = user.displayName() || username;
  return m("div", {
    className: "CanelaUserHero-names"
  }, m("h1", {
    className: "UserCard-identity CanelaUserHero-username"
  }, m("span", {
    className: "username"
  }, displayName), userIcons(user, 'profile'), badges), displayName !== username && m("h2", {
    className: "CanelaUserHero-displayName"
  }, username));
}
flarum.reg.add('ramon-canela', 'forum/user/installProfile', installProfile);